// config
import theme from 'config/theme';

// types
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';
import type { TFunction } from 'i18next';
import type { FlexMatchTaskResponse } from 'RestAPIs/flex-match/types';

export const getMatchStatusIcon = (
  row: FlexMatchTaskResponse,
  t: TFunction<'common', undefined>
): { icon: IconType; color: string; tooltip?: string } | null => {
  const { flexPortalMatchFlags, flexPortalMatchStatus, isAutoMatch } = row;

  const status = [
    {
      test: () => isAutoMatch,
      icon: 'double-sparkle' as IconType,
      color: theme.palette.info.main
    },
    {
      test: () => flexPortalMatchFlags.includes('ALL_REPLIED_UNAVAILABLE'),
      icon: 'user-xmark' as IconType,
      color: 'error',
      tooltip: t('replyAvailable')
    },
    {
      test: () => flexPortalMatchFlags.includes('ONE_REPLIED_AVAILABLE'),
      icon: 'user-check' as IconType,
      color: theme.vars.palette.success.onFixedVariant,
      tooltip: t('replyAvailable')
    },
    {
      test: () => flexPortalMatchFlags.includes('WAITING_FOR_REPLY'),
      icon: 'arrow-down-arrow-up' as IconType,
      color: theme.vars.palette.info.onContainer,
      tooltip: t('awaitingReply')
    },
    {
      test: () => flexPortalMatchStatus === 'ACCEPT_REQUEST',
      icon: 'user' as IconType,
      color: theme.palette.info.main
    }
  ];

  const match = status.find((match) => match.test());
  return match ?? null;
};

/**
 * Whether the request is open to an external organisation - what the briefcase marks.
 *
 * Read from the match actions rather than from the mail records: the agency mail is sent after the reply
 * and nothing waits for it, so a request that was just opened to an agency has its actions stored while its
 * notification row does not exist yet. Unretracted notifications stay a fallback for the rows whose actions
 * are empty - the backend synthesises the actions from those notifications in exactly that case.
 */
export const isOpenToAnAgency = (row: FlexMatchTaskResponse): boolean =>
  row.employeeCostCenterActions?.some(
    (action) =>
      action.employeeCostCenterType === 'EXTERNAL' &&
      action.action !== 'NONE' &&
      !!action.costCenterId
  ) || row.agenciesNotifications?.length > 0;
